import type { Directive } from 'vue';
import { useAssignedWarningStore } from '~/composables/stores/AssignedWarningStore';

type GuardedElement = HTMLElement & {
  __assignedWarningArId?: string | null;
  __assignedWarningHandler?: (e: Event) => void;
};

/**
 * `v-assigned-warning` / `v-assigned-warning="arId"` on an edit trigger (button, link, pencil...) of an
 * application: when someone else is already working on it (see AssignedWarningStore), the click is held
 * back until the user confirms the warning, then replayed on the same element.
 *
 * Listens in the capture phase so it runs before the element's own @click / NuxtLink navigation.
 */
const assignedWarning: Directive<GuardedElement, string | null | undefined> = {
  mounted(el, binding) {
    el.__assignedWarningArId = binding.value;
    el.__assignedWarningHandler = (e: Event) => {
      const store = useAssignedWarningStore();
      if (!store.needsWarning(el.__assignedWarningArId)) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      // replay on what was actually clicked (a checkbox label, an icon inside a link...) so it behaves the same
      const target = e.target instanceof HTMLElement && el.contains(e.target) ? e.target : el;
      store.guard(() => target.click(), el.__assignedWarningArId);
    };
    el.addEventListener('click', el.__assignedWarningHandler, true);
  },
  updated(el, binding) {
    el.__assignedWarningArId = binding.value;
  },
  unmounted(el) {
    if (el.__assignedWarningHandler) el.removeEventListener('click', el.__assignedWarningHandler, true);
  },
};

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.vueApp.directive('assigned-warning', assignedWarning);

  // Moving between the pages of the same application (show ↔ step edit ↔ household members) keeps the
  // confirmation; leaving the application closes it, so reopening it shows the warning again.
  useRouter().afterEach((to) => {
    const store = useAssignedWarningStore();
    if (store.applicationId && !to.path.startsWith(`/applications/${store.applicationId}/`)) store.reset('');
  });
});
